import React, { useState } from 'react';
import styled from 'styled-components';
import PageTemplate from '../components/templates/PageTemplate';
import Button from '../components/atoms/Button';
import { useForm } from '../hooks';
import { Link } from 'react-router-dom';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ProfileBanner = styled.div`
  width: 100%;
  height: 240px;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  margin-bottom: 60px;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  overflow: hidden;
  border: 4px solid ${({ theme }) => theme.colors.background.main};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarUpload = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 12px;
  padding: 4px;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.2s ease;
  opacity: 0;

  ${ProfileAvatar}:hover & {
    opacity: 1;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const ProfileName = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #9333EA;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  margin-left: 8px;
  font-weight: bold;
`;

const ProfileUsername = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
`;

const ProfileBio = styled.p`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 16px;
`;

const ReadMore = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const ProfileStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 600;
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
`;

const ShareButton = styled(Button)`
  min-width: 120px;
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ProfileContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ProfileSection = styled.div`
  width: 100%;
`;

const ProfileTabs = styled.div`
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 24px;
`;

const ProfileTab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.lg}`};
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(255, 255, 255, 0.6)')};
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  position: relative;
  cursor: pointer;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ $active }) => ($active ? '#9333EA' : 'transparent')};
    transition: background-color 0.2s ease;
  }

  &:hover {
    color: #fff;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  text-align: center;
`;

const EmptyStateIcon = styled.img`
  width: 120px;
  height: 120px;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  max-width: 400px;
  margin-bottom: 24px;
`;

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState('uploads');
  
  // Mock user data
  const userData = {
    firstName: 'Chris',
    lastName: 'Burkard',
    username: 'chrisburkard',
    bio: 'I love traveling and videography. Book me to get the most cinematic shots of resort on top travel platform',
    website: 'https://chrisburkard.com',
    twitter: '@chrisburkard',
    instagram: '@chrisburkard',
  };
  
  const { values } = useForm({
    initialValues: userData,
    onSubmit: () => {},
    validate: () => ({}),
  });

  return (
    <PageTemplate 
      title="Profile"
      hideHeaderOnMobile={true}
      mobileHeaderType={MobileHeaderType.TITLE_ONLY}
      mobileHeaderTitle="Profile"
    >
      <ProfileContainer>
        <ProfileBanner>
          <BannerImage src="/images/profile-banner.jpg" alt="Profile Banner" onError={(e) => {
            e.currentTarget.src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=2070";
          }} />
          <ProfileAvatar>
            <AvatarImage src="/images/profile-avatar.jpg" alt="Profile" onError={(e) => {
              e.currentTarget.src = "/images/default-avatar.png";
            }} />
          </ProfileAvatar>
        </ProfileBanner>
        
        <ProfileInfo>
          <ProfileName>
            Chris Burkard
            <VerifiedBadge>✓</VerifiedBadge>
          </ProfileName>
          <ProfileUsername>Professional Traveler</ProfileUsername>
          
          <ProfileBio>
            I love traveling and videography. Book me to get the most cinematic shots of resort on top travel platform
            <ReadMore> Read More...</ReadMore>
          </ProfileBio>
          
          <ProfileStats>
            <StatItem>
              <StatValue>97.2</StatValue>
              <StatLabel>Percentage</StatLabel>
            </StatItem>
            
            <StatItem>
              <StatValue>1200</StatValue>
              <StatLabel>Followers</StatLabel>
            </StatItem>
            
            <StatItem>
              <StatValue>251</StatValue>
              <StatLabel>Following</StatLabel>
            </StatItem>
          </ProfileStats>
          
          <ActionButtons>
            <Button variant="primary">Edit Profile</Button>
            <ShareButton variant="secondary">Share</ShareButton>
            <MoreButton>
              &#8942; {/* Vertical ellipsis */}
            </MoreButton>
          </ActionButtons>
        </ProfileInfo>
        
        <ProfileContent>
          <ProfileSection>
            <ProfileTabs>
              <ProfileTab
                $active={activeTab === 'uploads'}
                onClick={() => setActiveTab('uploads')}
              >
                Uploads
              </ProfileTab>
              <ProfileTab
                $active={activeTab === 'relationships'}
                onClick={() => setActiveTab('relationships')}
              >
                Relationships
              </ProfileTab>
            </ProfileTabs>
            
            {activeTab === 'uploads' && (
              <EmptyState>
                <EmptyStateIcon src="/images/empty-uploads.svg" onError={(e) => {
                  e.currentTarget.src = "/images/no-uploads.png";
                }} alt="No uploads" />
                <EmptyStateTitle>No uploads yet</EmptyStateTitle>
                <EmptyStateText>
                  You haven't uploaded any videos yet. Start creating and sharing your content with the world.
                </EmptyStateText>
                <Button variant="primary" as={Link} to="/upload">
                  Upload Video
                </Button>
              </EmptyState>
            )}
            
            {activeTab === 'relationships' && (
              <EmptyState>
                <EmptyStateIcon src="/images/empty-relationships.svg" onError={(e) => {
                  e.currentTarget.src = "/images/no-relationships.png";
                }} alt="No relationships" />
                <EmptyStateTitle>No relationships yet</EmptyStateTitle>
                <EmptyStateText>
                  You haven't established any relationships with enterprises yet. Connect with enterprises to expand your reach.
                </EmptyStateText>
                <Button variant="primary" as={Link} to="/enterprises">
                  Explore Enterprises
                </Button>
              </EmptyState>
            )}
          </ProfileSection>
        </ProfileContent>
      </ProfileContainer>
    </PageTemplate>
  );
};

export default Profile;
